import React, { FC, useEffect, useState } from 'react'
import _ from 'lodash'
// lib
import { isValidDate, parseDate } from 'data/lib/dates'
// styles
import { Checkbox_s, Dropdown_s, InputText_s } from 'styles/input-styles'
import { Label_s } from 'styles/app-styles'
import { DateText_s } from 'styles/report-styles'
// types
import {
  FolderReportProps,
  ReportFormParameters
} from 'components/folder/reports/reports'

type ReportType = 'cash_flow'
const displayName = 'Commercial Cash Flow'

type Cutoff = 'Analysis Term' | 'Analysis Term + 1'
type Periodicity = 1 | 3 | 12
type ReportStyle = 'simple' | 'recover_detail' | 'potential_breakout'

type CashFlowReportParameters = ReportFormParameters & {
  account_code: boolean
  asset_type: 'commercial'
  begindate: string // MM/DD/YYYY
  cutoff: Cutoff
  folder: boolean
  line_item_display: string
  period: Periodicity
  report_style: ReportStyle
  report: ReportType
  term: number
  totals: boolean
  total_metrics: boolean
}

const cutoffOptions = [
  { value: 'Analysis Term + 1'},
  { value: 'Analysis Term' },
]

const periodicityOptions = [
  { text: 'Annual', value: 12 },
  { text: 'Monthly', value: 1 },
  { text: 'Quarterly', value: 3 }
]

const styleOptions = [
  { text: 'Potential Breakout', value: 'potential_breakout' },
  { text: 'Simple', value: 'simple' }
]

export const propertyFilter = (property) => property.type === 'Commercial'

export const CommercialCashFlow: FC<FolderReportProps> = (props) => {
  const [cutoff, setCutoff] = useState<Cutoff>('Analysis Term + 1')
  const [includeAccountCode, setIncludeAccountCode] = useState<boolean>(false)
  const [includeAnnualTotals, setIncludeAnnualTotals] = useState<boolean>(false)
  const [lineItemDisplay, setLineItemDisplay] = useState<string>('')
  const [periodicity, updatePeriodicity] = useState<Periodicity>(12)
  const [startdate, setStartDate] = useState<string>(props.reportbegindate)
  const [style, setStyle] = useState<ReportStyle>('simple')
  const [term, setTerm] = useState<number>(props.term)
  const [termOnce, setTermOnce] = useState<boolean>(false)
  const [total_metrics, setTotalMetrics] = useState<boolean>(false)

  if (!term && props.term && !termOnce) {
    setTerm(props.term)
    setTermOnce(true)
  }

  useEffect(() => {
    if (props.lineItemDisplay[0]) setLineItemDisplay(props.lineItemDisplay[0]);
  }, [props.lineItemDisplay])

  useEffect(() => {
    if (disableIncludeAnnualTotals() && includeAnnualTotals) setIncludeAnnualTotals(false)
  }, [periodicity])

  useEffect(() => {
    if (periodicity !== 12 && includeAnnualTotals === false){
      setTotalMetrics(false)
    }
  }, [periodicity, includeAnnualTotals])

  useEffect(() => {
    if (startdate || !props.reportbegindate) return
    setStartDate(props.reportbegindate)
  }, [props.reportbegindate])

  useEffect(() => {
    const valid = isValidDate(startdate) && !_.isNil(term) && term >= 3

    if (disableIncludeAnnualTotals() && includeAnnualTotals) setIncludeAnnualTotals(false)

    props.setParameters(getParameters())
    props.setValid(valid)
  }, [includeAccountCode, includeAnnualTotals, periodicity, startdate, style, term, lineItemDisplay, cutoff, total_metrics])

  function disableIncludeAnnualTotals() {
    return periodicity == 12
  }

  function getParameters(): CashFlowReportParameters {
    return {
      account_code: includeAccountCode,
      asset_type: 'commercial',
      cutoff,
      folder: true,
      line_item_display: lineItemDisplay,
      period: periodicity,
      report_style: style,
      report: 'cash_flow',
      begindate: startdate,
      term: +term,
      totals: includeAnnualTotals,
      total_metrics
    }
  } 

  return (
    <>
      <div>
        <Label_s>Start Date</Label_s>
        <DateText_s
          value={startdate ? parseDate(startdate).format('MM/DD/YYYY') : props.reportbegindate}
          onChange={setStartDate}
          onBlur={setStartDate}
          persist={setStartDate}
          withoutDays
        />
      </div>
      <div>
        <Label_s>Term</Label_s>
        <InputText_s
          keyfilter="pnum"
          onChange={(e) => setTerm(e.target.value)}
          value={term}
        />
      </div>
      <div>
        <Label_s>Periodicity</Label_s>
        <Dropdown_s
          onChange={(e) => updatePeriodicity(e.value)}
          optionLabel="text"
          options={periodicityOptions}
          value={periodicity}
        />
      </div>
      <div>
        <Label_s>Style</Label_s>
        <Dropdown_s
          onChange={(e) => setStyle(e.value)}
          optionLabel="text"
          options={styleOptions}
          value={style}
        />
      </div>
      <div>
        <Label_s>Line Item Display</Label_s>
        <Dropdown_s
          onChange={(e) => setLineItemDisplay(e.value)}
          optionLabel="text"
          options={props.lineItemDisplay.map(item => { return {'text': item, 'value': item}} )}
          value={lineItemDisplay || props.lineItemDisplay[0]}
        />
      </div>
      <div>
        <Label_s>Display Account?</Label_s>
        <Checkbox_s
          checked={includeAccountCode}
          onChange={(e) => setIncludeAccountCode(e.checked)}
        />
      </div>
      <div>
        <Label_s>Display Annual Totals?</Label_s>
        <Checkbox_s
          checked={includeAnnualTotals}
          disabled={disableIncludeAnnualTotals()}
          onChange={(e) => setIncludeAnnualTotals(e.checked)}
        />
      </div>
      <div>
        <Label_s>Property Cutoff Date</Label_s>
        <Dropdown_s
          onChange={(e) => setCutoff(e.value)}
          optionLabel="value"
          options={cutoffOptions}
          value={cutoff}
        />
      </div>
      <div>
        <Label_s>Include Annual Metrics</Label_s>
        <Checkbox_s
          checked={total_metrics}
          disabled={includeAnnualTotals === false && periodicity !== 12}
          onChange={(e) => setTotalMetrics(e.checked)}
        />
      </div>  
    </>
  )
}

CommercialCashFlow.displayName = displayName
